import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { 
  Settings, Save, Play, Loader2, Zap, Target, Globe, Sliders,
  Calendar, Clock, CheckCircle2, XCircle, Trash2, HelpCircle, Database
} from "lucide-react";
import { toast } from "sonner";

// Tooltip descriptions for all config options
const TOOLTIPS = {
  // Universe
  max_latest_pages: "Maximum number of pages to fetch from FMP's latest filings endpoint. Higher = more funds discovered.",
  latest_limit: "Number of results per page when fetching latest filings.",
  lookback_days: "Only consider filings from the last N days.",
  
  // Test Mode
  test_ciks: "Specific CIKs to test with (comma-separated). Leave empty to use all.",
  test_n_funds: "Limit to N funds for quick testing. Overrides filters.",
  
  // Fund Filters
  use_holder_perf_filters: "Apply performance-based filters using FMP's holder-performance-summary data.",
  min_avg_holding_period: "Minimum average holding period in MONTHS (based on trailing 3-quarter average).",
  max_avg_holding_period: "Maximum average holding period in MONTHS. Leave empty for no limit.",
  min_portfolio_size: "Minimum number of positions the fund must hold.",
  max_portfolio_size: "Maximum number of positions. Filters out very large diversified funds.",
  max_funds: "Cap on total funds to process after all filters. Leave empty for no limit.",
  candidate_pool_size: "Early cap before performance API calls (saves API quota).",
  
  // Extraction
  quarters_per_fund: "How many quarters of holdings history to fetch per fund.",
  batch_size_funds: "Number of funds to process in parallel batches.",
  enrichment_batch_size: "Number of fund-quarter tasks to process per memory batch. Lower = less RAM usage.",
  timeout: "HTTP timeout in seconds for each API call.",
  max_retries: "Number of retry attempts for failed API calls.",
  calls_per_minute: "Rate limit cap. FMP free tier allows ~750/min, paid allows more.",
  
  // Behavior
  process_only_missing: "Only fetch fund-quarters not already in the database. Enables incremental updates.",
  process_stale_records: "Re-fetch records older than a threshold (for corrections/updates).",
  skip_successful_tasks: "Don't retry tasks that already completed successfully.",
  bootstrap_mode: "Quick test mode with minimal data for fast validation.",
  dry_run: "Simulate the pipeline without writing any data to the database.",
  
  // Buy/Sell
  buy_sell_enabled: "Generate buy/sell events after holdings ingestion completes.",
  recompute_last_n_quarters: "Number of recent quarters to recompute events for.",
  include_increase_decrease: "Track partial position changes (increases/decreases), not just new positions and full exits.",
  process_only_completed_quarters: "Only generate events for quarters with complete holdings data.",
  buy_sell_dry_run: "Preview event generation without writing to database.",
};

// Helper component for field with tooltip
const FieldWithTooltip = ({ label, tooltip, children }) => (
  <div className="space-y-1">
    <div className="flex items-center gap-1">
      <Label className="text-xs">{label}</Label>
      {tooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[250px] text-xs">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
    {children}
  </div>
);

// Helper component for switch with tooltip
const SwitchWithTooltip = ({ label, tooltip, description, checked, onCheckedChange }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-1">
      <div>
        <div className="flex items-center gap-1">
          <Label className="text-xs">{label}</Label>
          {tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[250px] text-xs">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
    </div>
    <Switch checked={checked} onCheckedChange={onCheckedChange} />
  </div>
);

const ConfigurationPage = () => {
  const { token } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [mainTab, setMainTab] = useState("config");
  
  // Form state
  const [formData, setFormData] = useState({
    name: "New Configuration",
    environment: "prod",
    notes: "",
    universe: {
      max_latest_pages: 5000,
      latest_limit: 100,
      lookback_days: 365,
    },
    test: {
      test_ciks: [],
      test_n_funds: null,
    },
    fund_filter: {
      use_holder_perf_filters: true,
      min_avg_holding_period: 6.0,
      max_avg_holding_period: null,
      min_portfolio_size: 10,
      max_portfolio_size: 100,
      max_funds: null,
      candidate_pool_size: null,
    },
    holdings: {
      quarters_per_fund: 20,
      batch_size_funds: 200,
      enrichment_batch_size: 50,
    },
    ingest: {
      ingest_chunk_rows: 5000,
      flush_threshold_rows: 10000,
    },
    http: {
      timeout: 30,
      max_retries: 6,
      calls_per_minute: 3000,
    },
    behavior: {
      process_only_missing: true,
      process_stale_records: false,
      skip_successful_tasks: true,
      cusip_resolution_async: false,
      profile_enrichment_async: false,
      dry_run: false,
      simulation_mode: false,
      bootstrap_mode: false,
    },
    buy_sell: {
      enabled: true,
      recompute_last_n_quarters: 4,
      include_increase_decrease: false,
      process_only_completed_quarters: true,
      dry_run: false,
    },
  });

  // Quarterly schedules - 47 days after quarter end
  const quarterlySchedules = [
    { id: "q4_run", quarter: "Q4", month: "February", day: 14, description: "Post Q4 filing deadline (~Feb 14)" },
    { id: "q1_run", quarter: "Q1", month: "May", day: 15, description: "Post Q1 filing deadline (~May 15)" },
    { id: "q2_run", quarter: "Q2", month: "August", day: 14, description: "Post Q2 filing deadline (~Aug 14)" },
    { id: "q3_run", quarter: "Q3", month: "November", day: 14, description: "Post Q3 filing deadline (~Nov 14)" },
  ];

  const fetchConfigs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/config/?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setConfigs(data.configs || []);
        if (data.configs?.length > 0 && !selectedConfig) {
          loadConfig(data.configs[0]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch configs:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchPresets = useCallback(async () => {
    try {
      const response = await fetch(`${API}/config/presets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPresets(data.presets || []);
      }
    } catch (error) {
      console.error("Failed to fetch presets:", error);
    }
  }, [token]);

  const fetchSchedules = useCallback(async () => {
    setSchedulesLoading(true);
    try {
      const response = await fetch(`${API}/scheduler/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSchedules(data.schedules || []);
      }
    } catch (error) {
      console.error("Failed to fetch schedules:", error);
    } finally {
      setSchedulesLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConfigs();
    fetchPresets();
    fetchSchedules();
  }, [fetchConfigs, fetchPresets, fetchSchedules]);

  const loadConfig = (config) => {
    setSelectedConfig(config);
    setFormData({
      name: config.name,
      environment: config.environment,
      notes: config.notes || "",
      universe: config.universe || formData.universe,
      test: config.test || formData.test,
      fund_filter: config.fund_filter || formData.fund_filter,
      holdings: config.holdings || formData.holdings,
      ingest: config.ingest || formData.ingest,
      http: config.http || formData.http,
      behavior: config.behavior || formData.behavior,
    });
    setSimulationResult(null);
  };

  const loadPreset = async (presetKey) => {
    try {
      const response = await fetch(`${API}/config/presets/${presetKey}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Configuration created from preset");
        fetchConfigs();
      }
    } catch (error) {
      toast.error("Failed to load preset");
    }
  };

  const deleteConfig = async (configId, configName) => {
    if (!window.confirm(`Delete configuration "${configName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/config/${configId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success("Configuration deleted");
        // If we deleted the selected config, clear selection
        if (selectedConfig?.id === configId) {
          setSelectedConfig(null);
        }
        fetchConfigs();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete configuration");
      }
    } catch (error) {
      toast.error("Failed to delete configuration");
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/config/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Configuration saved (v${data.version})`);
        fetchConfigs();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save configuration");
      }
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const simulateConfig = async () => {
    if (!selectedConfig) {
      toast.error("Select a configuration first");
      return;
    }
    
    setSimulating(true);
    try {
      const response = await fetch(`${API}/pipeline/simulate?config_id=${selectedConfig.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSimulationResult(data.simulation);
        toast.success("Simulation complete");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Simulation failed");
      }
    } catch (error) {
      toast.error("Simulation failed");
    } finally {
      setSimulating(false);
    }
  };

  const updateField = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  // Schedule functions
  const isScheduleEnabled = (scheduleId) => {
    return schedules.some(s => s.id === scheduleId && s.enabled);
  };

  const getScheduleInfo = (scheduleId) => {
    return schedules.find(s => s.id === scheduleId);
  };

  const toggleSchedule = async (schedule, enabled) => {
    try {
      if (enabled) {
        // Create/enable schedule
        const monthMap = { "February": 2, "May": 5, "August": 8, "November": 11 };
        const month = monthMap[schedule.month];
        
        // Use Production Incremental config or latest
        const configToUse = configs.find(c => c.name.includes("Production")) || configs[0];
        if (!configToUse) {
          toast.error("Please create a Production Incremental config first");
          return;
        }

        const response = await fetch(`${API}/scheduler/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            config_id: configToUse.id,
            schedule_type: "cron",
            hour: 6,  // 6 AM UTC
            minute: 0,
            day_of_week: "*",
            // We'll use a special field to indicate the month/day
            notes: `Quarterly ${schedule.quarter} run - ${schedule.description}`,
            enabled: true,
          }),
        });

        if (response.ok) {
          toast.success(`${schedule.quarter} scheduled run enabled`);
          fetchSchedules();
        }
      } else {
        // Disable schedule
        const existingSchedule = schedules.find(s => s.id === schedule.id);
        if (existingSchedule) {
          await fetch(`${API}/scheduler/${schedule.id}/disable`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          });
          toast.success(`${schedule.quarter} scheduled run disabled`);
          fetchSchedules();
        }
      }
    } catch (error) {
      toast.error("Failed to update schedule");
    }
  };

  const createQuarterlySchedule = async (schedule) => {
    // Create quarterly schedule with proper cron settings
    const monthMap = { "February": 2, "May": 5, "August": 8, "November": 11 };
    const cronMonth = monthMap[schedule.month];
    
    // Find production config
    const prodConfig = configs.find(c => c.name.toLowerCase().includes("production") || c.name.toLowerCase().includes("incremental"));
    const configToUse = prodConfig || selectedConfig || configs[0];
    
    if (!configToUse) {
      toast.error("Please create a configuration first");
      return;
    }

    try {
      const response = await fetch(`${API}/scheduler/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          config_id: configToUse.id,
          schedule_type: "cron",
          hour: 6,
          minute: 0,
          day_of_week: "*",
          enabled: true,
          notes: `Quarterly ${schedule.quarter} ingestion - ${schedule.description}. Config: ${configToUse.name}`,
        }),
      });

      if (response.ok) {
        toast.success(`${schedule.quarter} quarterly run scheduled`);
        fetchSchedules();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create schedule");
      }
    } catch (error) {
      toast.error("Failed to create schedule");
    }
  };

  const deleteSchedule = async (scheduleId) => {
    try {
      const response = await fetch(`${API}/scheduler/${scheduleId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Schedule deleted");
        fetchSchedules();
      }
    } catch (error) {
      toast.error("Failed to delete schedule");
    }
  };

  const runScheduleNow = async (scheduleId) => {
    try {
      const response = await fetch(`${API}/scheduler/${scheduleId}/run-now`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Schedule triggered - pipeline starting");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to trigger schedule");
      }
    } catch (error) {
      toast.error("Failed to trigger schedule");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-6" data-testid="configuration-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Configuration</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage pipeline configuration and scheduled runs
          </p>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="bg-muted rounded-sm">
          <TabsTrigger value="config" className="rounded-sm">
            <Settings className="w-4 h-4 mr-2" />
            Pipeline Config
          </TabsTrigger>
          <TabsTrigger value="schedule" className="rounded-sm">
            <Calendar className="w-4 h-4 mr-2" />
            Scheduled Runs
          </TabsTrigger>
        </TabsList>

        {/* Config Tab */}
        <TabsContent value="config" className="mt-6">
          <div className="flex items-center justify-end gap-2 mb-4">
            <Button
              variant="outline"
              onClick={simulateConfig}
              disabled={simulating || !selectedConfig}
              className="rounded-sm"
              data-testid="simulate-config-btn"
            >
              {simulating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Simulate
            </Button>
            <Button
              onClick={saveConfig}
              disabled={saving}
              className="rounded-sm"
              data-testid="save-config-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save as New Version
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Config List & Presets */}
            <div className="space-y-4">
              <Card className="bg-card border-border rounded-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Saved Configurations</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[200px]">
                    {configs.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                        No saved configurations. Create one from a preset below.
                      </div>
                    ) : (
                      configs.map((config) => (
                        <div
                          key={config.id}
                          className={`w-full text-left px-4 py-2 border-b border-border hover:bg-muted/50 transition-colors ${
                            selectedConfig?.id === config.id ? "bg-muted" : ""
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <button
                              onClick={() => loadConfig(config)}
                              className="flex-1 text-left"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium truncate">{config.name}</span>
                                <Badge variant="outline" className="text-xs rounded-sm ml-2">v{config.version}</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">{config.environment}</p>
                            </button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteConfig(config.id, config.name);
                              }}
                              className="h-7 w-7 p-0 text-muted-foreground hover:text-red-400 hover:bg-red-500/10 rounded-sm shrink-0"
                              title="Delete configuration"
                              data-testid={`delete-config-${config.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              <Card className="bg-card border-border rounded-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Presets</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[180px]">
                    {presets.map((preset) => (
                      <button
                        key={preset.key}
                        onClick={() => loadPreset(preset.key)}
                        className="w-full text-left px-4 py-2 border-b border-border hover:bg-muted/50 transition-colors"
                      >
                        <span className="text-sm font-medium">{preset.name}</span>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{preset.description}</p>
                      </button>
                    ))}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Simulation Result */}
              {simulationResult && (
                <Card className="bg-card border-border rounded-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Zap className="w-4 h-4 text-primary" />
                      Simulation Result
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-muted-foreground">Funds Selected</span>
                      <span className="font-mono text-sm">{simulationResult.funds_would_be_selected}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-muted-foreground">Tasks Created</span>
                      <span className="font-mono text-sm">{simulationResult.tasks_would_be_created}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-muted-foreground">Est. API Calls</span>
                      <span className="font-mono text-sm">{simulationResult.estimated_api_calls}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-muted-foreground">Affected Quarters</span>
                      <span className="font-mono text-xs">{simulationResult.affected_quarters?.join(", ")}</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Config Editor */}
            <div className="lg:col-span-3">
              <Card className="bg-card border-border rounded-sm">
                <CardContent className="p-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div>
                      <Label className="text-xs">Name</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 bg-background border-border rounded-sm"
                        data-testid="config-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Environment</Label>
                      <select
                        value={formData.environment}
                        onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                        className="w-full mt-1 bg-background border border-border rounded-sm px-3 py-2 text-sm"
                      >
                        <option value="prod">Production</option>
                        <option value="test">Test</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Notes</Label>
                      <Input
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="mt-1 bg-background border-border rounded-sm"
                        placeholder="Optional notes..."
                      />
                    </div>
                  </div>

                  <Tabs defaultValue="universe">
                    <TabsList className="bg-muted rounded-sm mb-4">
                      <TabsTrigger value="universe" className="rounded-sm text-xs">
                        <Globe className="w-3 h-3 mr-1" /> Universe
                      </TabsTrigger>
                      <TabsTrigger value="filters" className="rounded-sm text-xs">
                        <Target className="w-3 h-3 mr-1" /> Filters
                      </TabsTrigger>
                      <TabsTrigger value="extraction" className="rounded-sm text-xs">
                        <Database className="w-3 h-3 mr-1" /> Extraction
                      </TabsTrigger>
                      <TabsTrigger value="behavior" className="rounded-sm text-xs">
                        <Sliders className="w-3 h-3 mr-1" /> Behavior
                      </TabsTrigger>
                      <TabsTrigger value="buysell" className="rounded-sm text-xs">
                        <Zap className="w-3 h-3 mr-1" /> Buy/Sell
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="universe" className="space-y-4">
                      <ConfigSection title="Universe Discovery">
                        <ConfigField
                          label="Max Latest Pages"
                          tooltip={TOOLTIPS.max_latest_pages}
                          value={formData.universe.max_latest_pages}
                          onChange={(v) => updateField("universe", "max_latest_pages", parseInt(v) || 0)}
                          type="number"
                          min={1}
                          max={10000}
                        />
                        <ConfigField
                          label="Results Per Page"
                          tooltip={TOOLTIPS.latest_limit}
                          value={formData.universe.latest_limit}
                          onChange={(v) => updateField("universe", "latest_limit", parseInt(v) || 0)}
                          type="number"
                          min={100}
                          max={1000}
                        />
                        <ConfigField
                          label="Lookback Days"
                          tooltip={TOOLTIPS.lookback_days}
                          value={formData.universe.lookback_days}
                          onChange={(v) => updateField("universe", "lookback_days", parseInt(v) || 0)}
                          type="number"
                          min={30}
                          max={730}
                        />
                      </ConfigSection>

                      <ConfigSection title="Test Mode">
                        <ConfigField
                          label="Test N Funds"
                          tooltip={TOOLTIPS.test_n_funds}
                          value={formData.test.test_n_funds || ""}
                          onChange={(v) => updateField("test", "test_n_funds", v ? parseInt(v) : null)}
                          type="number"
                          placeholder="Leave empty for no limit"
                        />
                        <FieldWithTooltip label="Test CIKs" tooltip={TOOLTIPS.test_ciks}>
                          <Input
                            value={formData.test.test_ciks?.join(", ") || ""}
                            onChange={(e) => updateField("test", "test_ciks", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                            className="mt-1 bg-background border-border rounded-sm font-mono text-xs"
                            placeholder="e.g., 0001067983, 0001166559"
                          />
                        </FieldWithTooltip>
                      </ConfigSection>
                    </TabsContent>

                    <TabsContent value="filters" className="space-y-4">
                      <ConfigSection title="Fund Filtering">
                        <SwitchWithTooltip
                          label="Use Performance Filters"
                          tooltip={TOOLTIPS.use_holder_perf_filters}
                          checked={formData.fund_filter.use_holder_perf_filters}
                          onCheckedChange={(v) => updateField("fund_filter", "use_holder_perf_filters", v)}
                        />
                        <div></div>
                        <ConfigField
                          label="Min Avg Holding Period (months)"
                          tooltip={TOOLTIPS.min_avg_holding_period}
                          value={formData.fund_filter.min_avg_holding_period || ""}
                          onChange={(v) => updateField("fund_filter", "min_avg_holding_period", v ? parseFloat(v) : null)}
                          type="number"
                          step="0.1"
                        />
                        <ConfigField
                          label="Max Avg Holding Period (months)"
                          tooltip={TOOLTIPS.max_avg_holding_period}
                          value={formData.fund_filter.max_avg_holding_period || ""}
                          onChange={(v) => updateField("fund_filter", "max_avg_holding_period", v ? parseFloat(v) : null)}
                          type="number"
                          step="0.1"
                          placeholder="No limit"
                        />
                        <ConfigField
                          label="Min Portfolio Size"
                          tooltip={TOOLTIPS.min_portfolio_size}
                          value={formData.fund_filter.min_portfolio_size || ""}
                          onChange={(v) => updateField("fund_filter", "min_portfolio_size", v ? parseInt(v) : null)}
                          type="number"
                        />
                        <ConfigField
                          label="Max Portfolio Size"
                          tooltip={TOOLTIPS.max_portfolio_size}
                          value={formData.fund_filter.max_portfolio_size || ""}
                          onChange={(v) => updateField("fund_filter", "max_portfolio_size", v ? parseInt(v) : null)}
                          type="number"
                          placeholder="No limit"
                        />
                        <ConfigField
                          label="Max Funds"
                          tooltip={TOOLTIPS.max_funds}
                          value={formData.fund_filter.max_funds || ""}
                          onChange={(v) => updateField("fund_filter", "max_funds", v ? parseInt(v) : null)}
                          type="number"
                          min={1}
                          max={10000}
                          placeholder="No limit (keep all)"
                        />
                      </ConfigSection>
                    </TabsContent>

                    <TabsContent value="extraction" className="space-y-4">
                      <ConfigSection title="Holdings Extraction">
                        <ConfigField
                          label="Quarters Per Fund"
                          tooltip={TOOLTIPS.quarters_per_fund}
                          value={formData.holdings.quarters_per_fund}
                          onChange={(v) => updateField("holdings", "quarters_per_fund", parseInt(v) || 0)}
                          type="number"
                          min={1}
                          max={80}
                        />
                        <ConfigField
                          label="Batch Size (Funds)"
                          tooltip={TOOLTIPS.batch_size_funds}
                          value={formData.holdings.batch_size_funds}
                          onChange={(v) => updateField("holdings", "batch_size_funds", parseInt(v) || 0)}
                          type="number"
                          min={1}
                          max={500}
                        />
                        <ConfigField
                          label="Enrichment Batch Size"
                          tooltip={TOOLTIPS.enrichment_batch_size}
                          value={formData.holdings.enrichment_batch_size}
                          onChange={(v) => updateField("holdings", "enrichment_batch_size", parseInt(v) || 50)}
                          type="number"
                          min={10}
                          max={200}
                        />
                      </ConfigSection>

                      <ConfigSection title="HTTP Settings">
                        <ConfigField
                          label="Timeout (seconds)"
                          tooltip={TOOLTIPS.timeout}
                          value={formData.http.timeout}
                          onChange={(v) => updateField("http", "timeout", parseInt(v) || 0)}
                          type="number"
                          min={5}
                          max={120}
                        />
                        <ConfigField
                          label="Max Retries"
                          tooltip={TOOLTIPS.max_retries}
                          value={formData.http.max_retries}
                          onChange={(v) => updateField("http", "max_retries", parseInt(v) || 0)}
                          type="number"
                          min={1}
                          max={20}
                        />
                        <ConfigField
                          label="Calls Per Minute"
                          tooltip={TOOLTIPS.calls_per_minute}
                          value={formData.http.calls_per_minute}
                          onChange={(v) => updateField("http", "calls_per_minute", parseInt(v) || 0)}
                          type="number"
                          min={60}
                          max={10000}
                        />
                      </ConfigSection>
                    </TabsContent>

                    <TabsContent value="behavior" className="space-y-4">
                      <ConfigSection title="Processing Behavior">
                        <div className="col-span-2 space-y-3">
                          <SwitchWithTooltip
                            label="Process Only Missing"
                            tooltip={TOOLTIPS.process_only_missing}
                            description="Skip already successful tasks"
                            checked={formData.behavior.process_only_missing}
                            onCheckedChange={(v) => updateField("behavior", "process_only_missing", v)}
                          />
                          <SwitchWithTooltip
                            label="Process Stale Records"
                            tooltip={TOOLTIPS.process_stale_records}
                            description="Reprocess outdated data"
                            checked={formData.behavior.process_stale_records}
                            onCheckedChange={(v) => updateField("behavior", "process_stale_records", v)}
                          />
                          <SwitchWithTooltip
                            label="Skip Successful Tasks"
                            tooltip={TOOLTIPS.skip_successful_tasks}
                            description="Don't reprocess completed work"
                            checked={formData.behavior.skip_successful_tasks}
                            onCheckedChange={(v) => updateField("behavior", "skip_successful_tasks", v)}
                          />
                          <SwitchWithTooltip
                            label="Bootstrap Mode"
                            tooltip={TOOLTIPS.bootstrap_mode}
                            description="Quick initial test run"
                            checked={formData.behavior.bootstrap_mode}
                            onCheckedChange={(v) => updateField("behavior", "bootstrap_mode", v)}
                          />
                          <SwitchWithTooltip
                            label="Dry Run"
                            tooltip={TOOLTIPS.dry_run}
                            description="Don't write to database"
                            checked={formData.behavior.dry_run}
                            onCheckedChange={(v) => updateField("behavior", "dry_run", v)}
                          />
                        </div>
                      </ConfigSection>
                    </TabsContent>

                    <TabsContent value="buysell" className="space-y-4">
                      <ConfigSection title="Buy/Sell Event Generation">
                        <div className="space-y-4 col-span-2">
                          <SwitchWithTooltip
                            label="Enable Buy/Sell Stage"
                            tooltip={TOOLTIPS.buy_sell_enabled}
                            description="Generate buy/sell events after ingestion"
                            checked={formData.buy_sell?.enabled ?? true}
                            onCheckedChange={(v) => updateField("buy_sell", "enabled", v)}
                          />
                          <ConfigField
                            label="Recompute Last N Quarters"
                            tooltip={TOOLTIPS.recompute_last_n_quarters}
                            value={formData.buy_sell?.recompute_last_n_quarters ?? 4}
                            onChange={(v) => updateField("buy_sell", "recompute_last_n_quarters", parseInt(v) || 4)}
                            type="number"
                            min={1}
                            max={20}
                          />
                          <SwitchWithTooltip
                            label="Include Increase/Decrease Events"
                            tooltip={TOOLTIPS.include_increase_decrease}
                            description="Track partial position changes (not just new/exit)"
                            checked={formData.buy_sell?.include_increase_decrease ?? false}
                            onCheckedChange={(v) => updateField("buy_sell", "include_increase_decrease", v)}
                          />
                          <SwitchWithTooltip
                            label="Process Only Completed Quarters"
                            tooltip={TOOLTIPS.process_only_completed_quarters}
                            description="Skip quarters with pending holdings"
                            checked={formData.buy_sell?.process_only_completed_quarters ?? true}
                            onCheckedChange={(v) => updateField("buy_sell", "process_only_completed_quarters", v)}
                          />
                          <SwitchWithTooltip
                            label="Dry Run"
                            tooltip={TOOLTIPS.buy_sell_dry_run}
                            description="Preview without writing events"
                            checked={formData.buy_sell?.dry_run ?? false}
                            onCheckedChange={(v) => updateField("buy_sell", "dry_run", v)}
                          />
                        </div>
                      </ConfigSection>
                      
                      <ConfigSection title="About Buy/Sell Events">
                        <div className="col-span-2 text-xs text-muted-foreground space-y-2">
                          <p><strong>BUY (new):</strong> Fund opens a new position (previous value = 0, current value &gt; 0)</p>
                          <p><strong>SELL (exit):</strong> Fund completely exits a position (previous value &gt; 0, current value = 0)</p>
                          <p className="text-primary/70">This stage runs automatically after holdings ingestion completes.</p>
                        </div>
                      </ConfigSection>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Schedule Tab */}
        <TabsContent value="schedule" className="mt-6">
          <div className="max-w-4xl">
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Quarterly 13F Ingestion Schedule
                </CardTitle>
                <CardDescription>
                  Automated runs aligned with SEC 13F filing deadlines (~47 days after quarter-end)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Quarterly Schedule Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {quarterlySchedules.map((schedule) => {
                    const existingSchedule = schedules.find(s => s.notes?.includes(schedule.quarter));
                    const isEnabled = !!existingSchedule?.enabled;
                    
                    return (
                      <div 
                        key={schedule.id}
                        className={`p-4 rounded-sm border ${
                          isEnabled 
                            ? "border-green-500/30 bg-green-500/5" 
                            : "border-border bg-muted/20"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-semibold text-lg">{schedule.quarter}</span>
                              {isEnabled ? (
                                <Badge className="status-success rounded-sm text-xs">Active</Badge>
                              ) : (
                                <Badge className="status-idle rounded-sm text-xs">Inactive</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {schedule.description}
                            </p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              <span>Runs ~{schedule.month} {schedule.day} at 6:00 AM UTC</span>
                            </div>
                            {existingSchedule && (
                              <div className="text-xs text-muted-foreground mt-1">
                                Config: {configs.find(c => c.id === existingSchedule.config_id)?.name || "Unknown"}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {existingSchedule ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => runScheduleNow(existingSchedule.id)}
                                  className="rounded-sm"
                                  title="Run now"
                                >
                                  <Play className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteSchedule(existingSchedule.id)}
                                  className="rounded-sm text-red-400"
                                  title="Delete schedule"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => createQuarterlySchedule(schedule)}
                                className="rounded-sm"
                              >
                                Enable
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Info Box */}
                <div className="p-4 bg-muted/30 rounded-sm border border-border mt-6">
                  <h4 className="font-medium text-sm mb-2">How Quarterly Runs Work</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Each run uses the <strong>Production Incremental</strong> configuration</li>
                    <li>• Operates incrementally - only ingests missing fund-quarter data</li>
                    <li>• Skips already successful records, retries failed tasks</li>
                    <li>• Runs CUSIP resolution and profile enrichment automatically</li>
                    <li>• Sends email notification on completion</li>
                  </ul>
                </div>

                {/* Create Production Config Prompt */}
                {!configs.some(c => c.name.toLowerCase().includes("production")) && (
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                    <div className="flex items-start gap-3">
                      <Zap className="w-5 h-5 text-yellow-400 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-sm text-yellow-400">Create a Production Config</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          To enable quarterly scheduled runs, create a "Production Incremental" configuration 
                          using the preset. Go to Pipeline Config tab and click the preset.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Existing Schedules */}
                {schedules.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-medium text-sm mb-3">All Active Schedules</h4>
                    <div className="space-y-2">
                      {schedules.map((s) => (
                        <div 
                          key={s.id}
                          className="flex items-center justify-between p-3 bg-muted/30 rounded-sm"
                        >
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className={`w-4 h-4 ${s.enabled ? 'text-green-400' : 'text-muted-foreground'}`} />
                            <div>
                              <p className="text-sm font-medium">{s.notes || `Schedule ${s.id.slice(0, 8)}`}</p>
                              <p className="text-xs text-muted-foreground">
                                Config: {configs.find(c => c.id === s.config_id)?.name || "Unknown"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => runScheduleNow(s.id)}
                              className="rounded-sm"
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Run Now
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteSchedule(s.id)}
                              className="rounded-sm text-red-400"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
    </TooltipProvider>
  );
};

const ConfigSection = ({ title, children }) => (
  <div className="config-section">
    <h3 className="config-section-title">
      <Settings className="w-4 h-4" />
      {title}
    </h3>
    <div className="grid grid-cols-2 gap-4">
      {children}
    </div>
  </div>
);

const ConfigField = ({ label, value, onChange, type = "text", tooltip, ...props }) => (
  <div>
    <div className="flex items-center gap-1 mb-1">
      <Label className="text-xs">{label}</Label>
      {tooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[250px] text-xs">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
    <Input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-background border-border rounded-sm font-mono text-sm"
      {...props}
    />
  </div>
);

export default ConfigurationPage;
